import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import {
  MatAnchor,
  MatIconAnchor,
  MatIconButton,
} from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import {
  MatFormField,
  MatPrefix,
  MatSuffix,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';

import { NeedPermissionAnchorDirective } from '../../auth/need-permission-anchor.directive';
import { DarkModeToggleComponent } from '../../dark-mode/dark-mode-toggle/dark-mode-toggle.component';
import { UserMenuComponent } from '../user-menu/user-menu.component';
import { DashboardFavoritesService } from './dashboard-favorites.service';
import {
  DashboardCategories,
  DashboardCategory,
  DashboardTile,
} from './dashboard-items';

type DashboardTileWithZone = DashboardTile & {
  zone: 'workspace' | 'admin';
};

interface DashboardRenderGroup {
  label?: string;
  icon?: string;
  iconClass?: string;
  items: DashboardTileWithZone[];
}

interface DashboardRenderCategory {
  key: string;
  title: string;
  icon: string;
  zone: 'workspace' | 'admin';
  itemCount: number;
  groups: DashboardRenderGroup[];
}

/** Splits a category's (already filtered) tiles into its declared subsections, if any. */
function buildRenderGroups(
  category: DashboardCategory,
  items: DashboardTileWithZone[]
): DashboardRenderGroup[] {
  if (!category.groups?.length) {
    return items.length ? [{ items }] : [];
  }

  const groups: DashboardRenderGroup[] = [];
  const ungrouped = items.filter(
    tile => !category.groups!.some(g => g.label === tile.group)
  );
  if (ungrouped.length) {
    groups.push({ items: ungrouped });
  }

  for (const meta of category.groups) {
    const groupItems = items.filter(tile => tile.group === meta.label);
    if (groupItems.length) {
      groups.push({ ...meta, items: groupItems });
    }
  }

  return groups;
}

@Component({
  selector: 'glx-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: true,
  imports: [
    MatIcon,
    MatAnchor,
    MatIconButton,
    NeedPermissionAnchorDirective,
    RouterLink,
    MatIconAnchor,
    MatTooltip,
    MatDivider,
    MatFormField,
    MatInput,
    MatPrefix,
    MatSuffix,
    NgTemplateOutlet,
    DarkModeToggleComponent,
    UserMenuComponent,
  ],
})
export class DashboardComponent {
  favorites = inject(DashboardFavoritesService);

  searchTerm = signal('');

  private tilesByRoute = computed(() => {
    const map = new Map<string, DashboardTileWithZone>();
    for (const cat of DashboardCategories) {
      for (const item of cat.items) {
        map.set(item.route, { ...item, zone: cat.zone });
      }
    }
    return map;
  });

  favoriteTiles = computed(() =>
    this.favorites
      .favoriteRoutes()
      .map(route => this.tilesByRoute().get(route))
      .filter((t): t is DashboardTileWithZone => !!t)
  );

  filteredFavoriteTiles = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const tiles = this.favoriteTiles();
    return term
      ? tiles.filter(t => t.name.toLowerCase().includes(term))
      : tiles;
  });

  filteredCategories = computed<DashboardRenderCategory[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    return DashboardCategories.map(cat => {
      const items: DashboardTileWithZone[] = cat.items
        .filter(i => !term || i.name.toLowerCase().includes(term))
        .map(i => ({ ...i, zone: cat.zone }));
      return {
        key: cat.key,
        title: cat.title,
        icon: cat.icon,
        zone: cat.zone,
        itemCount: items.length,
        groups: buildRenderGroups(cat, items),
      };
    }).filter(cat => cat.itemCount > 0);
  });

  clearSearch(): void {
    this.searchTerm.set('');
  }

  toggleFavorite(event: Event, route: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.favorites.toggle(route);
  }
}
